import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { useLocation } from 'wouter';
import { MessageCircle, X, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '../../../convex/_generated/api';
import { capture, EVENTS } from '@/lib/posthog';
import { warn } from '@/lib/logger';
import { getCollectorSessionId } from '@/lib/collectorSession';
import {
  DRAG_KEY,
  DRAG_THRESHOLD_PX,
  clampToViewport,
  readDragPos,
  type Point,
} from '@/lib/conciergePanel';

// Re-exported because `__tests__/conciergeDrag.test.ts` imports them from here.
// The implementations moved to `@/lib/conciergePanel` so the agent panel can
// share them rather than growing a second copy.
export { clampToViewport, readDragPos };
import { ConciergeAgent } from './ConciergeAgent';
import {
  EMAIL_PROMPT,
  GREETING,

  TICKET_SENT,
  nodeById,
  type ConciergeNode,
} from '@/content/concierge';

/**
 * The concierge.
 *
 * THREE THINGS IT MUST DO, FROM THE BRIEF
 * ---------------------------------------
 * 1. Be movable — the visitor drags it wherever they want, and the position
 *    survives navigation and reloads.
 * 2. Get out of the way on its own — "auto-yield" dims and shrinks it while the
 *    page is scrolling, so it never sits on top of what someone is reading.
 * 3. Know who it is talking to — it reads the collector profile and SKIPS every
 *    question already answered, here or in the quiz or in the Combination.
 *
 * WHERE IT DELIBERATELY STOPS
 * ---------------------------
 * It never claims to see order status (Amazon owns fulfilment, we cannot), and
 * it never promises an email the deployment cannot send — if the send layer is
 * unconfigured it says the reference on screen is the only copy. Both are
 * cases where the comfortable line would be a lie the customer discovers later.
 */

type Corner = 'br' | 'bl';

interface Bubble {
  from: 'them' | 'you';
  text: string;
}

const POSITION_KEY = 'iv_concierge_corner';
const AUTOYIELD_KEY = 'iv_concierge_autoyield';
/** Routes where a floating panel would compete with the page's own job. */
const SUPPRESSED_PREFIXES = ['/admin', '/checkout', '/sign-in', '/create-account'];

function readCorner(): Corner {
  try {
    return window.localStorage.getItem(POSITION_KEY) === 'bl' ? 'bl' : 'br';
  } catch {
    return 'br';
  }
}

function readAutoYield(): boolean {
  try {
    // Defaults ON: the polite behaviour should be the one nobody has to find.
    return window.localStorage.getItem(AUTOYIELD_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function Concierge(): JSX.Element | null {
  /**
   * Agent mode, when the deployment has a model key.
   *
   * The scripted tree below is NOT dead code and must not be deleted — it is
   * what a deployment without `ANTHROPIC_API_KEY` serves, which is every local
   * checkout that has not set one and any restored deployment that has lost
   * it. `agentAvailable` fails closed for exactly that reason, so the failure
   * mode of a missing key is the old concierge rather than no concierge.
   *
   * `undefined` is still loading — render nothing rather than flashing the
   * scripted tree for a moment and then swapping it out underneath someone.
   */
  const agentAvailable = useQuery(api.conciergeAgent.agentAvailable, {});

  const [location, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [corner, setCorner] = useState<Corner>('br');
  /** A freely-dragged position. `null` means "still anchored to a corner". */
  const [dragPos, setDragPos] = useState<Point | null>(null);
  const [autoYield, setAutoYield] = useState(true);
  const [isYielding, setIsYielding] = useState(false);

  const [nodeId, setNodeId] = useState<string>('root');
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [draft, setDraft] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [needsEmail, setNeedsEmail] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [done, setDone] = useState(false);

  const sessionId = useMemo(() => getCollectorSessionId(), []);
  const profile = useQuery(api.collector.getMyProfile, { sessionId });
  const recordFact = useMutation(api.collector.recordFact);
  const createTicket = useMutation(api.tickets.createTicket);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  /** The element being positioned - needed for its measured size when clamping. */
  const shellRef = useRef<HTMLElement | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const yieldTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTicket = useRef<string>('');

  useEffect(() => {
    setCorner(readCorner());
    setAutoYield(readAutoYield());
    setDragPos(readDragPos());
  }, []);

  /**
   * Re-clamp when the window changes size.
   *
   * A position saved on a desktop window puts the panel off-screen on a phone,
   * and on rotate. Without this the panel becomes unreachable and the only
   * recovery is clearing site data.
   */
  useEffect(() => {
    if (!dragPos) {
      return;
    }
    const onResize = (): void => {
      const el = shellRef.current;
      if (!el) {
        return;
      }
      setDragPos((prev) =>
        prev ? clampToViewport(prev, el.offsetWidth, el.offsetHeight) : prev,
      );
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [dragPos]);

  /**
   * Auto-yield. While the page scrolls the panel fades and shrinks, then comes
   * back a beat after scrolling stops. Deliberately not a hide: something that
   * vanishes entirely feels broken, and you cannot click what you cannot see.
   */
  useEffect(() => {
    if (!autoYield || isOpen) {
      setIsYielding(false);
      return;
    }
    const onScroll = (): void => {
      setIsYielding(true);
      if (yieldTimer.current) {
        clearTimeout(yieldTimer.current);
      }
      yieldTimer.current = setTimeout(() => setIsYielding(false), 700);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (yieldTimer.current) {
        clearTimeout(yieldTimer.current);
      }
    };
  }, [autoYield, isOpen]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [bubbles, nodeId]);

  const knownKeys = useMemo(() => new Set(profile?.knownKeys ?? []), [profile?.knownKeys]);

  /**
   * Walk forward past anything we already know.
   *
   * This is the "feels like listening" mechanic: if the quiz already learned
   * their collection size, the concierge does not ask for it again — it moves
   * to the next thing it genuinely does not know.
   */
  const resolveNode = useCallback(
    (startId: string | null): ConciergeNode | null => {
      let current = startId;
      const seen = new Set<string>();
      while (current) {
        if (seen.has(current)) {
          return null; // cycle guard
        }
        seen.add(current);
        const node = nodeById(current);
        if (!node) {
          return null;
        }
        if (node.fillsKey && knownKeys.has(node.fillsKey) && node.options?.length) {
          current = node.options[0].next;
          continue;
        }
        return node;
      }
      return null;
    },
    [knownKeys],
  );

  const openPanel = useCallback((): void => {
    setIsOpen(true);
    capture(EVENTS.CONCIERGE_OPENED, {
      path: window.location.pathname,
      is_known: Boolean(profile?.archetype),
    });
    if (bubbles.length === 0) {
      const name = profile?.firstName;
      const greeting = !name
        ? GREETING.stranger
        : profile?.archetype
          ? GREETING.knownWithArchetype.replace('{name}', name)
          : GREETING.known.replace('{name}', name);
      setBubbles([{ from: 'them', text: greeting }]);
      const first = resolveNode('root');
      if (first) {
        setNodeId(first.id);
      }
    }
  }, [bubbles.length, profile?.archetype, profile?.firstName, resolveNode]);

  const choose = useCallback(
    async (label: string, value: string | undefined, next: string | null): Promise<void> => {
      const node = nodeById(nodeId);
      setBubbles((prev) => [...prev, { from: 'you', text: label }]);
      capture(EVENTS.CONCIERGE_ANSWERED, { node: nodeId });

      if (node?.fillsKey && value) {
        // Fire and forget: a failed write must never stall the conversation.
        void recordFact({
          sessionId,
          key: node.fillsKey,
          value,
          source: 'concierge',
        }).catch((cause: unknown) => {
          warn('Concierge fact not recorded', {
            reason: cause instanceof Error ? cause.message : String(cause),
          });
        });
      }

      if (next === 'toProduct') {
        setLocation('/products/binder-216');
      }

      if (next === null) {
        const closing = nodeById('closing');
        setBubbles((prev) => [...prev, { from: 'them', text: closing?.says ?? 'Any time.' }]);
        setDone(true);
        return;
      }

      const resolved = resolveNode(next);
      if (!resolved) {
        setDone(true);
        return;
      }
      setNodeId(resolved.id);
      setBubbles((prev) => [...prev, { from: 'them', text: resolved.says }]);
    },
    [nodeId, recordFact, resolveNode, sessionId, setLocation],
  );

  const submitTicket = useCallback(
    async (message: string, email?: string): Promise<void> => {
      setIsSending(true);
      try {
        const response = await createTicket({
          message,
          email,
          source: 'concierge',
          page: window.location.pathname,
          sessionId,
        });
        capture(EVENTS.CONCIERGE_TICKET_CREATED, { path: window.location.pathname });
        setBubbles((prev) => [
          ...prev,
          { from: 'them', text: TICKET_SENT.confirmation.replace('{ref}', response.ticketRef) },
        ]);
        setNeedsEmail(false);
        setDone(true);
      } catch (cause) {
        const reason = cause instanceof Error ? cause.message : String(cause);
        // A signed-out visitor with no address: ask, rather than failing.
        if (reason.includes('email address')) {
          setNeedsEmail(true);
          setBubbles((prev) => [...prev, { from: 'them', text: EMAIL_PROMPT.says }]);
        } else {
          setBubbles((prev) => [...prev, { from: 'them', text: reason }]);
        }
      } finally {
        setIsSending(false);
      }
    },
    [createTicket, sessionId],
  );

  /**
   * Tap the handle: snap to the other corner, discarding any dragged position.
   *
   * Tap and drag are deliberately different verbs on the same control - "tap to
   * snap, drag to place" - which also leaves a keyboard-only route to moving the
   * panel, since a drag-only handle is unusable without a pointer.
   */
  const toggleCorner = useCallback((): void => {
    const next: Corner = corner === 'br' ? 'bl' : 'br';
    setCorner(next);
    setDragPos(null);
    try {
      window.localStorage.setItem(POSITION_KEY, next);
      window.localStorage.removeItem(DRAG_KEY);
    } catch {
      // Position is a convenience; losing it is not worth surfacing.
    }
  }, [corner]);

  /**
   * Free drag, on POINTER events rather than mouse events.
   *
   * Pointer events cover touch, pen and mouse in one path, so this works on a
   * phone. Mouse handlers would have left the handle dead on exactly the device
   * where a floating panel is most in the way - and the header renders a grip
   * icon, which is the universal promise that a thing can be dragged.
   */
  const onHandlePointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>): void => {
    const shell = shellRef.current;
    if (!shell) {
      return;
    }
    const rect = shell.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: rect.left,
      originY: rect.top,
      moved: false,
    };
    // Capture so a fast drag that outruns the handle keeps sending us moves.
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onHandlePointerMove = useCallback((event: React.PointerEvent<HTMLButtonElement>): void => {
    const drag = dragRef.current;
    const shell = shellRef.current;
    if (!drag || !shell || drag.pointerId !== event.pointerId) {
      return;
    }
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
      return;
    }
    drag.moved = true;
    setDragPos(
      clampToViewport(
        { x: drag.originX + dx, y: drag.originY + dy },
        shell.offsetWidth,
        shell.offsetHeight,
      ),
    );
  }, []);

  const onHandlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>): void => {
      const drag = dragRef.current;
      dragRef.current = null;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Already released - not worth surfacing.
      }
      if (!drag.moved) {
        // A tap, not a drag. Fall through to the corner toggle.
        toggleCorner();
        return;
      }
      setDragPos((current) => {
        if (current) {
          try {
            window.localStorage.setItem(DRAG_KEY, JSON.stringify(current));
          } catch {
            // As above: position is a convenience.
          }
        }
        return current;
      });
    },
    [toggleCorner],
  );

  const onHandlePointerCancel = useCallback((): void => {
    dragRef.current = null;
  }, []);

  const toggleAutoYield = useCallback((): void => {
    const next = !autoYield;
    setAutoYield(next);
    try {
      window.localStorage.setItem(AUTOYIELD_KEY, next ? 'on' : 'off');
    } catch {
      // As above.
    }
  }, [autoYield]);

  if (agentAvailable === undefined) {
    return null;
  }
  if (agentAvailable) {
    return <ConciergeAgent />;
  }

  if (SUPPRESSED_PREFIXES.some((prefix) => location.startsWith(prefix))) {
    return null;
  }

  const node = nodeById(nodeId);
  const side = corner === 'br' ? 'right-4 md:right-6' : 'left-4 md:left-6';
  // A dragged position wins over the corner anchor. Both renders use these so
  // the panel and the collapsed bubble cannot disagree about where "here" is.
  const anchorClass = dragPos ? '' : `bottom-5 ${side}`;
  const anchorStyle = dragPos ? { left: dragPos.x, top: dragPos.y } : undefined;

  if (!isOpen) {
    return (
      <button
        type="button"
        ref={(el) => {
          shellRef.current = el;
        }}
        style={anchorStyle}
        onClick={openPanel}
        aria-label="Open the concierge"
        className={`fixed ${anchorClass} z-40 flex items-center gap-2 rounded-full border border-[hsla(39,47%,60%,0.35)] bg-[var(--surface-1)] px-4 py-3 shadow-lg transition-all duration-300 hover:border-[var(--brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand)] ${
          isYielding ? 'scale-90 opacity-30' : 'scale-100 opacity-100'
        }`}
      >
        <MessageCircle className="h-4 w-4 text-[var(--brand)]" />
        <span className="text-sm text-[var(--brand)]">Ask us</span>
      </button>
    );
  }

  return (
    <div
      ref={(el) => {
        shellRef.current = el;
      }}
      style={anchorStyle}
      className={`fixed ${anchorClass} z-40 flex max-h-[70vh] w-[min(360px,calc(100vw-2rem))] flex-col overflow-hidden rounded border border-[var(--surface-3)] bg-[var(--surface-1)] shadow-2xl`}
      role="dialog"
      aria-label="Concierge"
    >
      <div className="flex items-center justify-between border-b border-[var(--surface-3)] px-3 py-2">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={onHandlePointerUp}
            onPointerCancel={onHandlePointerCancel}
            onKeyDown={(event) => {
              // Keyboard route to the same thing a tap does. Without this the
              // handle is pointer-only, and a drag-only control is unusable
              // for anyone navigating by keyboard.
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggleCorner();
              }
            }}
            aria-label="Drag to move the concierge, or press to send it to the other corner"
            title="Drag to move · press to snap to the other corner"
            // `touch-action: none` stops the browser scrolling the page instead
            // of giving us the pointer moves. Without it the drag does nothing
            // on a phone, which is where it matters most.
            style={{ touchAction: 'none' }}
            className="cursor-grab rounded p-1 text-muted-foreground transition-colors hover:text-[var(--brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--brand)] active:cursor-grabbing"
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <span className="font-display text-sm tracking-wide text-[var(--brand)]">Concierge</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggleAutoYield}
            className="rounded px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground transition-colors hover:text-[var(--brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--brand)]"
            title="Slide out of the way while you scroll"
          >
            {autoYield ? 'Auto-move: on' : 'Auto-move: off'}
          </button>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label="Close the concierge"
            className="rounded p-1 text-muted-foreground transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--brand)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-2.5 overflow-y-auto px-3 py-3">
        {bubbles.map((bubble, index) => (
          <div
            key={index}
            className={`max-w-[85%] rounded px-3 py-2 text-sm leading-relaxed ${
              bubble.from === 'them'
                ? 'bg-[var(--surface-2)] text-foreground'
                : 'ml-auto bg-[var(--brand)] text-[var(--vault-black)]'
            }`}
          >
            {bubble.text}
          </div>
        ))}
      </div>

      {!done && (
        <div className="border-t border-[var(--surface-3)] px-3 py-3">
          {needsEmail ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (emailDraft.trim()) {
                  void submitTicket(pendingTicket.current, emailDraft.trim());
                }
              }}
              className="flex gap-2"
            >
              <Input
                type="email"
                required
                value={emailDraft}
                onChange={(event) => setEmailDraft(event.target.value)}
                placeholder={EMAIL_PROMPT.placeholder}
                className="flex-1 bg-[var(--surface-2)] text-sm"
              />
              <Button type="submit" size="sm" disabled={isSending}>
                {EMAIL_PROMPT.submitLabel}
              </Button>
            </form>
          ) : node?.freeText ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                const message = draft.trim();
                if (!message) {
                  return;
                }
                pendingTicket.current = message;
                setBubbles((prev) => [...prev, { from: 'you', text: message }]);
                setDraft('');
                void submitTicket(message);
              }}
              className="flex gap-2"
            >
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={node.freeText.placeholder}
                className="flex-1 bg-[var(--surface-2)] text-sm"
              />
              <Button type="submit" size="sm" disabled={isSending}>
                {node.freeText.submitLabel}
              </Button>
            </form>
          ) : (
            <div className="flex flex-col gap-2">
              {node?.options?.map((option) => (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => void choose(option.label, option.value, option.next)}
                  className="rounded-sm border border-[var(--surface-3)] bg-[var(--surface-2)] px-3 py-2 text-left text-sm transition-colors hover:border-[var(--brand)] hover:bg-[var(--surface-3)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand)]"
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
